import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import {
  ProjectTaskDependencyType,
  addProjectTaskDependency,
  listProjectTaskDependencies,
  projectTaskDependencyTypeNames,
  removeProjectTaskDependency,
} from '../../api/projects';
import type {
  ProjectTaskDependencyItem,
  ProjectTaskDependencyTypeValue,
  ProjectTaskItem,
} from '../../api/projects';
import { dependencyTypeOptions } from './taskDisplay';

/** Giá trị của biểu mẫu khai quan hệ phụ thuộc. */
interface DependencyFormValues {
  readonly predecessorTaskId: string;
  readonly successorTaskId: string;
  readonly dependencyType: ProjectTaskDependencyTypeValue;
  readonly lagDays: number;
  readonly isHardConstraint: boolean;
  readonly note: string | null;
}

/**
 * Hộp thoại quản lý quan hệ phụ thuộc giữa các công việc.
 *
 * <b>Vòng phụ thuộc do máy chủ chặn, và đó là lớp bảo vệ duy nhất.</b> Phát hiện vòng cần duyệt cả đồ
 * thị — A chờ B, B chờ C, C chờ lại A — thứ không ràng buộc nào của cơ sở dữ liệu diễn đạt được, và
 * cũng không phải thứ giao diện nên tự tính lại. Khi bị từ chối thì thông báo của máy chủ đã nói rõ
 * nguyên nhân, nên hộp thoại chỉ hiển thị lại nguyên văn.
 *
 * <b>Độ trễ âm là giá trị hợp lệ</b>, không phải lỗi nhập: nó diễn đạt hai việc chồng lấp nhau — bắt
 * đầu viết tài liệu khi việc lắp đặt còn ba ngày nữa mới xong.
 */
export function ProjectTaskDependencyModal({
  projectId,
  tasks,
  canManage,
  open,
  onClose,
}: {
  readonly projectId: string;
  readonly tasks: readonly ProjectTaskItem[];
  readonly canManage: boolean;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const [form] = Form.useForm<DependencyFormValues>();

  const [dependencies, setDependencies] =
    useState<readonly ProjectTaskDependencyItem[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!open) {
      return;
    }

    let isCurrent = true;

    async function load(): Promise<void> {
      setErrorMessage(null);

      try {
        const loaded = await listProjectTaskDependencies(projectId);

        if (isCurrent) {
          setDependencies(loaded);
        }
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được quan hệ phụ thuộc.',
        );
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [projectId, open, reloadToken]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  async function submit(values: DependencyFormValues): Promise<void> {
    setIsSaving(true);

    try {
      await addProjectTaskDependency(
        projectId,
        values.predecessorTaskId,
        values.successorTaskId,
        values.dependencyType,
        values.lagDays,
        values.isHardConstraint,
        values.note ?? null,
      );

      message.success('Đã khai quan hệ phụ thuộc.');
      form.resetFields();
      reload();
    } catch (error) {
      // Thông báo của máy chủ đã nói rõ nguyên nhân, kể cả trường hợp vòng phụ thuộc — hiển thị lại
      // nguyên văn thay vì diễn đạt lại, vì diễn đạt lại là cách làm mất thông tin.
      message.error(
        error instanceof ApiError ? error.message : 'Không khai được quan hệ phụ thuộc.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function remove(dependencyId: string): Promise<void> {
    try {
      await removeProjectTaskDependency(projectId, dependencyId);

      message.success('Đã xóa quan hệ phụ thuộc.');
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không xóa được.');
    }
  }

  /** Tên hiển thị của một công việc, hoặc chính định danh khi không tra được. */
  function taskLabel(taskId: string): string {
    const task = tasks.find((candidate) => candidate.id === taskId);

    return task === undefined ? taskId : `${task.wbsCode} — ${task.name}`;
  }

  const columns: TableColumnsType<ProjectTaskDependencyItem> = [
    {
      title: 'Việc trước',
      dataIndex: 'predecessorTaskId',
      render: (taskId: string) => taskLabel(taskId),
    },
    {
      title: 'Kiểu',
      dataIndex: 'dependencyType',
      width: 160,
      render: (dependencyType: ProjectTaskDependencyTypeValue, dependency) => (
        <Space size={4}>
          <span>{projectTaskDependencyTypeNames[dependencyType]}</span>
          {dependency.isHardConstraint ? <Tag color="red">Cứng</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Việc sau',
      dataIndex: 'successorTaskId',
      render: (taskId: string) => taskLabel(taskId),
    },
    {
      title: 'Trễ (ngày)',
      dataIndex: 'lagDays',
      width: 110,
      align: 'right',
      render: (lagDays: number) => (lagDays === 0 ? '—' : lagDays),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, dependency) => (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          disabled={!canManage}
          onClick={() => {
            void remove(dependency.id);
          }}
        />
      ),
    },
  ];

  const taskOptions = tasks.map((task) => ({
    value: task.id,
    label: `${task.wbsCode} — ${task.name}`,
  }));

  return (
    <Modal
      open={open}
      title="Quan hệ phụ thuộc giữa các công việc"
      width={900}
      footer={null}
      onCancel={onClose}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {errorMessage !== null ? <Alert type="error" showIcon message={errorMessage} /> : null}

        <Table<ProjectTaskDependencyItem>
          rowKey="id"
          size="small"
          pagination={false}
          columns={columns}
          dataSource={dependencies as ProjectTaskDependencyItem[] | undefined}
          loading={dependencies === null && errorMessage === null}
        />

        {canManage ? (
          <Form<DependencyFormValues>
            form={form}
            layout="vertical"
            initialValues={{
              dependencyType: ProjectTaskDependencyType.FinishToStart,
              lagDays: 0,
              isHardConstraint: false,
            }}
            onFinish={(values) => void submit(values)}
          >
            <Space wrap align="start" size="middle">
              <Form.Item
                name="predecessorTaskId"
                label="Việc trước"
                rules={[{ required: true, message: 'Chưa chọn việc trước.' }]}
                style={{ minWidth: 260 }}
              >
                <Select showSearch optionFilterProp="label" options={taskOptions} />
              </Form.Item>
              <Form.Item name="dependencyType" label="Kiểu" style={{ minWidth: 180 }}>
                <Select<ProjectTaskDependencyTypeValue> options={dependencyTypeOptions} />
              </Form.Item>
              <Form.Item
                name="successorTaskId"
                label="Việc sau"
                rules={[{ required: true, message: 'Chưa chọn việc sau.' }]}
                style={{ minWidth: 260 }}
              >
                <Select showSearch optionFilterProp="label" options={taskOptions} />
              </Form.Item>
              <Form.Item
                name="lagDays"
                label="Trễ (ngày)"
                extra="Số âm nghĩa là hai việc chồng lấp nhau."
              >
                <InputNumber<number> min={-90} max={90} style={{ width: 120 }} />
              </Form.Item>
              <Form.Item
                name="isHardConstraint"
                label="Ràng buộc cứng"
                valuePropName="checked"
                extra="Không thể vi phạm, ví dụ không thể chạy thử trước khi lắp xong."
              >
                <Checkbox>Bắt buộc</Checkbox>
              </Form.Item>
              <Form.Item name="note" label="Ghi chú" style={{ minWidth: 220 }}>
                <Input />
              </Form.Item>
              <Form.Item label=" ">
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<PlusOutlined />}
                  loading={isSaving}
                >
                  Thêm
                </Button>
              </Form.Item>
            </Space>
          </Form>
        ) : null}

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Hệ thống chặn vòng phụ thuộc: nếu quan hệ mới làm một việc gián tiếp phải chờ chính nó, yêu
          cầu sẽ bị từ chối kèm giải thích.
        </Typography.Text>
      </Space>
    </Modal>
  );
}
